import image from "../../Images/StaticWebsite.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const StaticWebsite = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
            const handleScroll = () => {
                const sections = document.querySelectorAll("[data-section]");
                let current = "";
    
                sections.forEach((section) => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.clientHeight;
                    if (window.pageYOffset >= sectionTop - 100) {
                        current = section.getAttribute("data-section");
                    }
                });
    
                setActiveSection(current);
            };
    
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }, []);
    
        const scrollToSection = (sectionId) => {
            const element = document.querySelector(`[data-section="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                    <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">
                    

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>
            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Static Website Stack`}
                    </ReactMarkdown>
                </div>
                <div className="image-container" data-section="architecture">
                    <img className="image" src={image} />
                </div>
                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **Route 53**: AWS's scalable DNS and domain routing service that directs user requests
- **CloudFront**: Content Delivery Network (CDN) that caches and distributes content globally from edge locations
- **S3**: Object storage service hosting static website files (HTML, CSS, JavaScript, images)

## Use Cases
- Static websites and single-page applications (React, Vue, Angular)
- Marketing and landing pages with global audience
- Documentation sites and blogs
- Portfolio and company websites
- Any web application that can be served as static files

## Cost
- **Route 53**: $0.50 per hosted zone/month + $0.40 per million queries
  - Typical usage: ~$1-2/month
- **CloudFront**: $0.085 per GB for first 10TB + 0.0075-0.016 per 10,000 requests (varies by region)
  - Light traffic (10GB, 1M requests): ~$1-2/month
  - Medium traffic (100GB, 10M requests): ~$10-15/month
  - High traffic (1TB, 100M requests): ~$85-120/month
- **S3**: $0.023 per GB storage + $0.005 per 1,000 PUT requests + $0.0004 per 1,000 GET requests
  - Small site (5GB, 100K requests): ~$1/month
  - Medium site (50GB, 1M requests): ~$2-3/month
                        `}
                    </ReactMarkdown>
                </div>
                <div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                    <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import 'source-map-support/register';
import { StaticWebsiteStack } from '../lib/static_website_stack';

const app = new cdk.App();

new StaticWebsiteStack(app, "S3", {
  enableCloudFront: false,
  env: {
    account: process.env.CDK_DEFAULT_ACCOUNT,
    region: process.env.CDK_DEFAULT_REGION,
  }
})

new StaticWebsiteStack(app, "Cloudfront", {
  enableCloudFront: true,
  env: {
    account: process.env.CDK_DEFAULT_ACCOUNT,
    region: process.env.CDK_DEFAULT_REGION,
  }
})
`}
                    </SyntaxHighlighter>
                </div>
                    
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# static-website-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as s3 from "aws-cdk-lib/aws-s3";
import * as cloudfront from 'aws-cdk-lib/aws-cloudfront';
import * as origins from 'aws-cdk-lib/aws-cloudfront-origins';
import * as acm from 'aws-cdk-lib/aws-certificatemanager'

interface StaticWebsiteStackProps extends cdk.StackProps {
  domainName?: string;
  certificateArn?: string;
  enableCloudFront?: boolean;
}

export class StaticWebsiteStack extends cdk.Stack {
  
  constructor(scope: Construct, id: string, props?: StaticWebsiteStackProps) {
    super(scope, id, props);

    const websiteBucket = new s3.Bucket(this, "WebsiteBucket", {
      websiteIndexDocument: "index.html",
      publicReadAccess: !props?.enableCloudFront,
      blockPublicAccess: props?.enableCloudFront ? s3.BlockPublicAccess.BLOCK_ALL : s3.BlockPublicAccess.BLOCK_ACLS_ONLY,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
      autoDeleteObjects: true,
    })

    if (props?.enableCloudFront) {
      const distribution = new cloudfront.Distribution(this, "Distribution", {
        defaultBehavior: {
          origin: origins.S3BucketOrigin.withOriginAccessControl(websiteBucket),
          viewerProtocolPolicy: cloudfront.ViewerProtocolPolicy.REDIRECT_TO_HTTPS,      
        },
        defaultRootObject: 'index.html',
        domainNames: props?.domainName ? [props.domainName] : undefined,
        certificate: props?.certificateArn ? acm.Certificate.fromCertificateArn(this, 'Cert', props.certificateArn) : undefined,
        errorResponses: [{
          httpStatus: 404,
          responseHttpStatus: 404,
          responsePagePath: '/404.html',
      }]
      
      })

      new cdk.CfnOutput(this, "DistributionDomainName", {
        value: distribution.distributionDomainName,
      })
    } else {
      new cdk.CfnOutput(this, "WebsiteURL", {
        value: websiteBucket.bucketWebsiteUrl,
      })
    }
    new cdk.CfnOutput(this, 'BucketName', {
      value: websiteBucket.bucketName
    })

  
  }
}            
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as s3 from "aws-cdk-lib/aws-s3";
import * as cloudfront from 'aws-cdk-lib/aws-cloudfront';
import * as origins from 'aws-cdk-lib/aws-cloudfront-origins';
import * as acm from 'aws-cdk-lib/aws-certificatemanager'

interface StaticWebsiteStackProps extends cdk.StackProps {
  domainName?: string;
  certificateArn?: string;
  enableCloudFront?: boolean;
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-s3** - S3 bucket constructs for creating and configuring object storage
- **aws-cloudfront** - CloudFront distribution constructs for CDN functionality
- **aws-cloudfront-origins** - Origin configurations that connect CloudFront to backend sources like S3
- **aws-certificatemanager** - ACM certificate constructs for SSL/TLS certificates
## Props
- **domainName**: custom domain name for the website, e.g. myapp.com
- **certificateArn**: ARN existing ACM certificate for HTTPS support
- **enableCloudFront**: flag to enable or disable cloudfront in from of the S3 bucket 

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & S3 Bucket`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class StaticWebsiteStack extends cdk.Stack {
  
  constructor(scope: Construct, id: string, props?: StaticWebsiteStackProps) {
    super(scope, id, props);

    const websiteBucket = new s3.Bucket(this, "WebsiteBucket", {
      websiteIndexDocument: "index.html",
      publicReadAccess: !props?.enableCloudFront,
      blockPublicAccess: props?.enableCloudFront ? s3.BlockPublicAccess.BLOCK_ALL : s3.BlockPublicAccess.BLOCK_ACLS_ONLY,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
      autoDeleteObjects: true,
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## S3 Bucket
- **websiteIndexDocument** - Sets "index.html" as the default document served when accessing the bucket as a website
- **publicReadAccess** - Enables public read access only when CloudFront is disabled (direct S3 website hosting)
- **blockPublicAccess** - Blocks all public access if using CloudFront (CloudFront will access via OAC), otherwise only blocks public ACLs
- **removalPolicy: DESTROY** - Automatically deletes the bucket when the stack is destroyed (useful for dev/testing)
- **autoDeleteObjects: true** - Removes all objects in the bucket before deletion to allow successful bucket removal
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Cloudfront`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    if (props?.enableCloudFront) {
      const distribution = new cloudfront.Distribution(this, "Distribution", {
        defaultBehavior: {
          origin: origins.S3BucketOrigin.withOriginAccessControl(websiteBucket),
          viewerProtocolPolicy: cloudfront.ViewerProtocolPolicy.REDIRECT_TO_HTTPS,      
        },
        defaultRootObject: 'index.html',
        domainNames: props?.domainName ? [props.domainName] : undefined,
        certificate: props?.certificateArn ? acm.Certificate.fromCertificateArn(this, 'Cert', props.certificateArn) : undefined,
        errorResponses: [{
          httpStatus: 404,
          responseHttpStatus: 404,
          responsePagePath: '/404.html',
      }]
      
      })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Cloudfront
- Cloudfront distribution is only created when enableCloudFront prop is true
- **origin** - Connects CloudFront to the S3 bucket using Origin Access Control (OAC) for secure, private access
- **viewerProtocolPolicy** - Automatically redirects all HTTP requests to HTTPS for secure connections
- **defaultRootObject** - Serves "index.html" when users access the root URL of the distribution
- **domainNames** - Associates the custom domain name with the distribution if provided, otherwise uses CloudFront's default domain
- **certificate** - Imports and attaches an existing ACM certificate by ARN for custom domain HTTPS support, if provided
- **errorResponses** - Configures a custom 404 error page at "/404.html" to be displayed when resources are not found
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
      new cdk.CfnOutput(this, "DistributionDomainName", {
        value: distribution.distributionDomainName,
      })
    } else {
      new cdk.CfnOutput(this, "WebsiteURL", {
        value: websiteBucket.bucketWebsiteUrl,
      })
    }
    new cdk.CfnOutput(this, 'BucketName', {
      value: websiteBucket.bucketName
    })

  
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )

}
export default StaticWebsite;